import React, { useEffect, useState } from "react";
import { api, resolveImageSrc } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { S } from "./styles";

/**
 * Cart + COD checkout. Multiple items → one order id. No payment gateway:
 * the order is recorded, HealthCoins are deposited, and the shopkeeper gets a
 * pre-filled WhatsApp message + the order in their Orders panel.
 */
export default function CartModal({ user, onClose, onPlaced, onTrack, showToast }) {
  const { items, setQty, remove, clear, subtotal } = useCart();
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [note, setNote] = useState("");
  const [coords, setCoords] = useState(null);
  const [coordsStatus, setCoordsStatus] = useState("pending");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [walletAvail, setWalletAvail] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const primaryShopId = items[0]?.shop_id;

  useEffect(() => {
    if (!primaryShopId) { setWalletAvail(0); return; }
    api.get(`/wallet/applicable?shop_id=${primaryShopId}`).then((r) => setWalletAvail(r.data?.applicable || 0)).catch(() => setWalletAvail(0));
  }, [primaryShopId]);

  useEffect(() => {
    if (!navigator?.geolocation) { setCoordsStatus("denied"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setCoordsStatus("ok"); },
      () => setCoordsStatus("denied"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, []);

  const haversineKm = (a, b) => {
    if (!a || !b || typeof b.lat !== "number" || typeof b.lng !== "number") return null;
    const R = 6371, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  };
  const farItem = coords ? items.find((it) => { const d = haversineKm(coords, { lat: it.lat, lng: it.lng }); return d != null && d > 5; }) : null;
  const walletUse = useWallet ? Math.min(walletAvail, subtotal) : 0;
  const codDue = subtotal - walletUse;

  const place = async () => {
    if (!items.length) return;
    if (!address.trim()) { showToast?.("Please enter your delivery address."); return; }
    if (farItem) { showToast?.(`"${farItem.name}" is beyond the 5 km delivery range.`); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/cart", {
        items: items.map((it) => ({ product_id: it.id, qty: it.qty })),
        delivery_address: address.trim(),
        delivery_lat: coords?.lat,
        delivery_lng: coords?.lng,
        contact_mobile: mobile.trim() || undefined,
        note: note.trim() || undefined,
        use_wallet: useWallet,
      });
      setPlaced(data);
      onPlaced?.(data);
      clear();
    } catch (e) {
      showToast?.(e?.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  const wrap = (children) => (
    <div data-testid="cart-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.55)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "22px 22px 0 0", maxWidth: 480, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: "#E0DED2", borderRadius: 4, margin: "10px auto" }} />
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );

  // ---- Order placed confirmation ----
  if (placed) {
    const d0 = (placed.shop_phone || "").replace(/\D/g, "");
    const waDigits = d0.length === 10 ? "91" + d0 : d0;
    const lines = (placed.items || []).map((i) => `• ${i.product_name} x${i.qty} — ₹${i.line_total}`).join("\n");
    const waMsg = `🛒 New Mapbazar order #${placed.id.slice(0, 8)}\n${lines}\nTotal: ₹${placed.total} (Cash on Delivery)\nDeliver to: ${placed.delivery_address}\nCustomer: ${placed.user_name}${placed.user_mobile ? ` (${placed.user_mobile})` : ""}`;
    const waUrl = waDigits.length >= 11 ? `https://wa.me/${waDigits}?text=${encodeURIComponent(waMsg)}` : `https://wa.me/?text=${encodeURIComponent(waMsg)}`;
    return wrap(
      <div data-testid="cart-confirmation">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 44 }}>✅</div>
          <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 800, marginTop: 6 }}>Order placed!</div>
          <div data-testid="cart-order-id" style={{ marginTop: 4, fontSize: 13, color: "#666" }}>
            Order ID <b style={{ color: "#0F3D2E" }}>#{placed.id.slice(0, 8).toUpperCase()}</b>
          </div>
        </div>
        <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 12, background: "#FFF9F0", border: "1px solid #F3CC8B", fontSize: 13, color: "#5C3D12", fontWeight: 600 }}>
          💵 Pay <b>₹{placed.total}</b> cash on delivery when your order arrives.
        </div>
        {Number(placed.points_awarded) > 0 && (
          <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 12, background: "#E8F5EE", border: "1px solid #9BC4A8", fontSize: 13, color: "#0F3D2E", fontWeight: 700 }}>
            🌱 +{placed.points_awarded} HealthCoins added to your wallet.
          </div>
        )}
        <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="cart-notify-shop-btn" className="notranslate"
          style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: 13, borderRadius: 100, border: "none", background: "#25D366", color: "white", fontWeight: 800, fontSize: 14, textDecoration: "none", boxSizing: "border-box" }}>
          💬 Send order to shop on WhatsApp
        </a>
        <button data-testid="cart-track-order-btn" onClick={() => { onTrack?.(placed.id); }}
          style={{ marginTop: 8, width: "100%", padding: 13, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
          🛵 Track my order
        </button>
        <button data-testid="cart-done-btn" onClick={onClose}
          style={{ marginTop: 8, width: "100%", padding: 12, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", color: "#333", fontWeight: 600, cursor: "pointer" }}>
          Done
        </button>
      </div>
    );
  }

  // ---- Empty cart ----
  if (!items.length) {
    return wrap(
      <div data-testid="cart-empty" style={{ textAlign: "center", padding: "20px 0 8px" }}>
        <div style={{ fontSize: 40 }}>🛒</div>
        <div style={{ ...S.fraunces, fontSize: 20, fontWeight: 700, marginTop: 6 }}>Your cart is empty</div>
        <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>Add products from the map to order with Cash on Delivery.</div>
        <button data-testid="cart-close-btn" onClick={onClose} style={{ marginTop: 16, padding: "11px 22px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer" }}>Browse shops</button>
      </div>
    );
  }

  // ---- Cart + checkout ----
  return wrap(
    <>
      <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Your cart</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it) => {
          const img = resolveImageSrc(it.image);
          return (
            <div key={it.id} data-testid={`cart-item-${it.id}`} style={{ display: "flex", gap: 10, padding: 10, background: "#FAFAF5", border: "1px solid #EAE8DC", borderRadius: 12 }}>
              {img && (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/api/")) && (
                <img src={img} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>{it.shop_name || "Local shop"}</div>
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #D5D3C7", borderRadius: 100, overflow: "hidden" }}>
                    <button data-testid={`cart-dec-${it.id}`} onClick={() => (it.qty <= 1 ? remove(it.id) : setQty(it.id, it.qty - 1))} style={{ border: "none", background: "white", width: 26, height: 26, fontSize: 15, cursor: "pointer" }}>−</button>
                    <span data-testid={`cart-qty-${it.id}`} style={{ minWidth: 22, textAlign: "center", fontSize: 12.5, fontWeight: 700 }}>{it.qty}</span>
                    <button data-testid={`cart-inc-${it.id}`} onClick={() => setQty(it.id, it.qty + 1)} style={{ border: "none", background: "white", width: 26, height: 26, fontSize: 15, cursor: "pointer" }}>+</button>
                  </div>
                  <div style={{ marginLeft: "auto", ...S.fraunces, fontSize: 15, fontWeight: 800, color: "#0F3D2E" }}>₹{it.price * it.qty}</div>
                  <button data-testid={`cart-remove-${it.id}`} onClick={() => remove(it.id)} aria-label="Remove" style={{ border: "none", background: "transparent", color: "#B4452A", cursor: "pointer", fontSize: 14 }}>🗑️</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, color: "#666", fontWeight: 600 }}>Subtotal</span>
        <span data-testid="cart-subtotal" style={{ ...S.fraunces, fontSize: 20, fontWeight: 800, color: "#0F3D2E" }}>₹{subtotal}</span>
      </div>

      <div data-testid="cart-cod-coin-note" style={{ marginTop: 8, fontSize: 11.5, color: "#8C5A1C", background: "#FFF9F0", border: "1px solid #F3E2C0", borderRadius: 10, padding: "8px 11px", lineHeight: 1.5 }}>
        🛡️ HealthCoins are earned on <b>prepaid orders only</b> — COD orders don't earn insurance savings.
      </div>

      <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#666", display: "block", marginTop: 14 }}>DELIVERY ADDRESS</label>
      <textarea data-testid="cart-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="House / flat no, street, landmark, area…" rows={2}
        style={{ width: "100%", marginTop: 6, padding: 12, borderRadius: 12, border: "1.5px solid #E0DED2", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />

      <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#666", display: "block", marginTop: 10 }}>CONTACT MOBILE</label>
      <input data-testid="cart-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile (optional)"
        style={{ width: "100%", marginTop: 6, padding: 12, borderRadius: 12, border: "1.5px solid #E0DED2", fontSize: 14, boxSizing: "border-box" }} />

      <div data-testid="cart-gps-status" style={{ marginTop: 12, padding: 10, borderRadius: 10, fontSize: 12, fontWeight: 600,
        background: coordsStatus === "ok" ? "#E8F5EE" : coordsStatus === "denied" ? "#FFEFEF" : "#FFF9F0",
        color: coordsStatus === "ok" ? "#0F3D2E" : coordsStatus === "denied" ? "#9A2C2C" : "#5C3D12",
        border: `1px solid ${coordsStatus === "ok" ? "#9BC4A8" : coordsStatus === "denied" ? "#F3B6B6" : "#F3CC8B"}` }}>
        {coordsStatus === "ok" && <>📍 Location captured</>}
        {coordsStatus === "pending" && <>📍 Getting your location…</>}
        {coordsStatus === "denied" && <>📍 Location off — no problem, we'll confirm your address by phone. You can still place the order.</>}
      </div>
      {farItem && (
        <div data-testid="cart-geofence-warning" style={{ marginTop: 10, padding: 10, borderRadius: 10, fontSize: 12, fontWeight: 600, background: "#FFEFEF", color: "#9A2C2C", border: "1px solid #F3B6B6" }}>
          🚫 "{farItem.name}" is beyond the 5 km delivery range. Remove it to continue.
        </div>
      )}

      {walletAvail > 0 && (
        <label data-testid="cart-wallet-toggle" style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 10, background: "#EEF6FF", border: "1px solid #BBD1F5", cursor: "pointer" }}>
          <input type="checkbox" data-testid="cart-wallet-checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1453A6" }}>👛 Use wallet · ₹{walletAvail} available{useWallet && walletUse > 0 ? ` — applying ₹${walletUse}` : ""}</span>
        </label>
      )}

      <button data-testid="cart-place-order-btn" onClick={place} disabled={placing || !address.trim() || !!farItem}
        style={{ marginTop: 16, width: "100%", padding: 14, borderRadius: 100, border: "none",
          background: (placing || !address.trim() || !!farItem) ? "#9DB3A4" : "#0F3D2E",
          color: "white", fontWeight: 800, fontSize: 15, cursor: (placing || !address.trim() || !!farItem) ? "default" : "pointer" }}>
        {placing ? "Placing order…" : `🛵 Place order · ₹${codDue} COD${walletUse > 0 ? ` (₹${walletUse} wallet)` : ""}`}
      </button>
      <button data-testid="cart-keep-shopping-btn" onClick={onClose} style={{ marginTop: 8, width: "100%", padding: 12, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", color: "#333", fontWeight: 600, cursor: "pointer" }}>Keep shopping</button>
    </>
  );
}
